"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConsolidatedReportsTable from "../../../components/ConsolidatedReportsTable/ConsolidatedReportsTable";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";
import { getBudgetReport, getAllUnits } from "../../../redux/unitSlice/unitSlice";
import ActiveProject from "../../../assets/icons/Region.svg";
import BudgetReportTable from "./BudgetReportTable";
import { Select } from "antd";

/* -------------------------
   Helpers
------------------------- */
// const DEFAULT_FY = "2025-2026";

const getCurrentFY = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0 = Jan

    const fyStartYear = month >= 3 ? year : year - 1; // April-March FY

    return `${fyStartYear}-${fyStartYear + 1}`;
};

const DEFAULT_FY = getCurrentFY();

const quarterMap = {
    Apr: "Q1", May: "Q1", Jun: "Q1",
    Jul: "Q2", Aug: "Q2", Sep: "Q2",
    Oct: "Q3", Nov: "Q3", Dec: "Q3",
    Jan: "Q4", Feb: "Q4", Mar: "Q4",
};

const parseNumber = (v) =>
    Number(v?.toString().replace(/,/g, "")) || 0;

const toLakhs = (v) => parseNumber(v) / 100;

const getStartYearFromFY = (fy) => Number(fy?.split("-")[0]);

const getFYStartYear = (month, year) =>
    ["Jan", "Feb", "Mar"].includes(month) ? year - 1 : year;

const getFYLabel = (fy) =>
    `FY ${String(fy).slice(2)}-${String(fy + 1).slice(2)}`;

const formatNumber = (val) =>
    val ? val.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "--";

/* -------------------------
   Component
------------------------- */
const BudgetWiseReport = () => {
    const dispatch = useDispatch();
    const { budgetReportDetails, loading, allUnitsList } =
        useSelector((state) => state.unit);

    const [selectedFY, setSelectedFY] = useState(DEFAULT_FY);
    const [selectedUnitRegion, setSelectedUnitRegion] =
        useState("Select Unit/Region");

    const selectedFYStartYear = useMemo(
        () => getStartYearFromFY(selectedFY),
        [selectedFY]
    );

    /* -------------------------
       API Calls
    ------------------------- */
    useEffect(() => {
        if (!selectedFYStartYear) return;

        const payload = {
            from_year: selectedFYStartYear,
            to_year: selectedFYStartYear + 1,
        };

        if (selectedUnitRegion !== "Select Unit/Region") {
            payload.mnf_unit = selectedUnitRegion;
        }

        dispatch(getBudgetReport(payload));
    }, [dispatch, selectedFYStartYear, selectedUnitRegion]);

    useEffect(() => {
        dispatch(getAllUnits());
    }, [dispatch]);

    const data = budgetReportDetails || {};

    /* -------------------------
       Quarter Mapping
    ------------------------- */
    const quarters = useMemo(() => {
        if (!data?.revenue || !selectedFYStartYear) return [];

        const map = {};

        Object.keys(data.revenue)
            .filter((k) => !k.startsWith("Q") && k !== "Total")
            .forEach((key) => {
                const [month, year] = key.split("-");
                const fyStart = getFYStartYear(month, Number(year));
                if (fyStart !== selectedFYStartYear) return;

                const q = quarterMap[month];
                const qKey = `${q}-${fyStart}`;

                map[qKey] ??= { quarter: qKey, months: [] };
                map[qKey].months.push(key);
            });

        return Object.values(map);
    }, [data, selectedFYStartYear]);

    /* -------------------------
       Columns
    ------------------------- */
    const columns = useMemo(() => {
        if (!quarters.length) return [];

        return [
            {
                title: "Parameter",
                dataIndex: "label",
                key: "label",
                fixed: "left",
                width: 220,
            },
            ...quarters.map((q) => {
                const fy = q.quarter.split("-")[1];

                return {
                    title: `Q${q.quarter[1]} ${getFYLabel(Number(fy))}`,
                    children: [
                        ...q.months.map((m) => ({
                            title: m.split("-")[0],
                            dataIndex: m,
                            key: m,
                            width: 90,
                            align: "right",
                        })),
                        {
                            title: "Total",
                            dataIndex: q.quarter,
                            key: q.quarter,
                            width: 90,
                            align: "right",
                        },
                    ],
                };
            }),
            {
                title: "TOTAL",
                dataIndex: "TOTAL",
                key: "TOTAL",
                fixed: "right",
                width: 90,
                align: "right",
            },
        ];
    }, [quarters]);

    /* -------------------------
       Table Data (All in Lakhs)
    ------------------------- */
    const tableData = useMemo(() => {
        if (!data?.revenue || !quarters.length) return [];

        const metrics = [
            {
                key: "revenue",
                label: "I. Revenue",
            },
            {
                key: "totalOperationalIncome",
                label: "II Other Operational Income",
            },
            {
                key: "revenueFromOperations",
                label: "Revenue from Operations (RFO) (I+II)",
                calculated: true,
                bold: true,
            },
            {
                key: "DM_COST",
                label: "III. Material Cost",
            },
            {
                key: "conversionCost",
                label: (
                    <>
                        IV. Conversion Cost <i>(Inc. CO)</i>
                    </>
                ),
                calculated: true,
            },
            {
                key: "apportionedCost",
                label: "V.Other Apportioned Cost",
            },
            {
                key: "projectCost",
                label: "Total Cost (III + IV+ V)",
                bold: true,
                calculated: true,
            },
            {
                key: "DM",
                label: "DM (%)",
            },
            {
                key: "projectProfit",
                label: "Margin (FC level) [ I - (III + IV)]",
                bold: true,
                calculated: true,
            },
            {
                key: "profit_loss",
                label: "Profit / Loss (RFO - TC)",
                bold: true,
                calculated: true,
            },
            {
                key: "grossBilling",
                label: "Gross Billing",
            },
            {
                key: "totalNetBilling",
                label: "Net Billing",
            },
            {
                key: "totalCashInflow",
                label: "Cash Inflow",
            },
            {
                key: "totalCashOutflow",
                label: "Cash Outflow",
            },
            {
                key: "netCashFlow",
                label: "Cash Netflow",
            },
        ];

        return metrics.map((metric) => {
            const row = {
                key: metric.key,
                label: metric.bold ? (
                    <strong style={{ fontSize: "16px" }}>{metric.label}</strong>
                ) : (
                    <span style={{ fontSize: "16px" }}>{metric.label}</span>
                ),
                calculated: metric.calculated,
            };
            let overallTotal = 0;

            quarters.forEach((q) => {
                let quarterSum = 0;

                q.months.forEach((m) => {
                    if (metric.key === "DM") {
                        const dm = parseNumber(data.DM?.[m]);
                        row[m] = dm ? `${dm.toFixed(2)}%` : "--";
                    }
                    else if (metric.key === "revenueFromOperations") {
                        const value =
                            parseNumber(data.revenue?.[m]) +
                            parseNumber(data.totalOperationalIncome?.[m]);

                        row[m] = formatNumber(value);
                        quarterSum += value;
                    }
                    else if (metric.key === "conversionCost") {
                        const value =
                            parseNumber(data.projectCost?.[m]) -
                            parseNumber(data.DM_COST?.[m]);

                        row[m] = formatNumber(value);
                        quarterSum += value;
                    }

                    //Total Cost
                    else if (metric.key === "projectCost") {
                        const value =
                            parseNumber(data.DM_COST?.[m]) +
                            (
                                parseNumber(data.projectCost?.[m]) -
                                parseNumber(data.DM_COST?.[m])
                            ) +
                            parseNumber(data.apportionedCost?.[m]);

                        row[m] = formatNumber(value);
                        quarterSum += value;
                    }

                    //Margin FC level
                    else if (metric.key === "projectProfit") {
                        const value =
                            parseNumber(data.revenue?.[m]) -
                            (
                                parseNumber(data.DM_COST?.[m]) +
                                (
                                    parseNumber(data.projectCost?.[m]) -
                                    parseNumber(data.DM_COST?.[m])
                                )
                            );

                        row[m] = formatNumber(value);
                        quarterSum += value;
                    }
                    else if (metric.key === "profit_loss") {
                        const revenueFromOperations =
                            parseNumber(data.revenue?.[m]) +
                            parseNumber(data.totalOperationalIncome?.[m]);

                        const totalCost =
                            parseNumber(data.projectCost?.[m]) +
                            parseNumber(data.apportionedCost?.[m]);

                        const value = revenueFromOperations - totalCost;

                        row[m] = formatNumber(value);
                        quarterSum += value;
                    }
                    else {
                        const value = parseNumber(data[metric.key]?.[m]);
                        row[m] = formatNumber(value);
                        quarterSum += value;
                    }
                });

                if (metric.key === "DM") {
                    let revenueSum = 0;
                    let costSum = 0;

                    q.months.forEach((m) => {
                        revenueSum += parseNumber(data.revenue?.[m]);
                        costSum += parseNumber(data.DM_COST?.[m]);
                    });

                    if (costSum > 0) {
                        const quarterDM = (costSum / revenueSum) * 100;
                        row[q.quarter] = `${quarterDM.toFixed(2)}%`;
                    } else {
                        row[q.quarter] = "--";
                    }
                } else {
                    row[q.quarter] = formatNumber(quarterSum);
                    overallTotal += quarterSum;
                }
            });

            if (metric.key === "DM") {
                const dm = parseNumber(data.DM?.Total);
                row.TOTAL = dm ? `${dm.toFixed(2)}%` : "--";
            } else {
                row.TOTAL = formatNumber(overallTotal);
            }

            return row;
        });
    }, [data, quarters]);

    /* -------------------------
       UI
    ------------------------- */
    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <div className="loader w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-5">
            <div className="flex gap-3 items-center">
                <img src={ActiveProject} alt="region" />
                <div>
                    <div className="text-[24px] font-semibold">
                        {/* {sessionStorage.getItem("role")==="ADMIN" ? "BHEL" : JSON.parse(sessionStorage.getItem("user"))?.email.split("_").join(" ")} */}
                        Performance Plan - Key Parameters
                    </div>
                    <div className="text-gray-500 text-sm">
                        * All figures are in Rupees (in Crores)
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
                <div className="flex justify-end gap-3 mb-4">
                    {sessionStorage.getItem("role")?.toLowerCase() === "admin" && (
                        <CustomSelectWithFilter
                            showSearch="true"
                            data={
                                Array.isArray(allUnitsList)
                                    ? allUnitsList.map((value) => ({
                                        label: value?.UNIT_DISPLAY_NAME,
                                        value: value?.MNF_UNIT,
                                    }))
                                    : []
                            }
                            onChange={(value) => {
                                setSelectedUnitRegion(value)
                            }}
                            value={selectedUnitRegion}
                            placeholder="Select Unit/Region"
                            className="w-[15rem]"
                        />
                    )}

                    <Select
                        className="w-[12rem]"
                        value={selectedFY}
                        onChange={setSelectedFY}
                        options={[
                            { label: "FY 2023-24", value: "2023-2024" },
                            { label: "FY 2024-25", value: "2024-2025" },
                            { label: "FY 2025-26", value: "2025-2026" },
                            { label: "FY 2026-27", value: "2026-2027" },
                            { label: "FY 2027-28", value: "2027-2028" },
                            { label: "FY 2028-29", value: "2028-2029" },
                            { label: "FY 2029-30", value: "2029-2030" },
                        ]}
                    />
                </div>

                {/* <ConsolidatedReportsTable
                    columns={columns}
                    dataSource={tableData}
                    scroll={{ x: "max-content", y: 800 }}
                    sticky
                /> */}

                <BudgetReportTable
                    columns={columns}
                    dataSource={tableData}
                />
            </div>
        </div>
    );
};

export default BudgetWiseReport;
